import {
  forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef,
  type CSSProperties, type ElementType, type HTMLAttributes, type ReactNode, type Ref, type RefObject
} from 'react';
import { Glide } from './glide.js';
import type { GlideEvent, GlideOptions } from './types.js';

export interface ScrollAreaHandlers {
  onScroll?: (e: GlideEvent) => void;
  onReachEnd?: (e: GlideEvent) => void;
  onReachStart?: (e: GlideEvent) => void;
  onReachRight?: (e: GlideEvent) => void;
  onReachLeft?: (e: GlideEvent) => void;
  onProgress?: (e: GlideEvent) => void;
  onBottomStateChange?: (atBottom: boolean, e: GlideEvent) => void;
  onResize?: (e: GlideEvent) => void;
}

export interface ScrollAreaProps
  extends GlideOptions,
    ScrollAreaHandlers,
    Omit<HTMLAttributes<HTMLElement>, 'children' | 'className' | 'style' | 'id' | 'onScroll' | 'onProgress' | 'onResize'> {
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** Class and style for the scrolling element itself (padding goes here, not on the host). */
  viewportClassName?: string;
  viewportStyle?: CSSProperties;
  /** Attributes for the scrolling element: a `role`, an `aria-label`, an `onKeyDown` for a listbox. */
  viewportProps?: HTMLAttributes<HTMLDivElement>;
  /** The scrolling element, for code that reads `scrollTop` or scrolls a child into view. */
  viewportRef?: Ref<HTMLDivElement>;
  /** The engine, for `scrollTo`, `getState`, `setMarkers` and the rest. */
  glideRef?: Ref<Glide | null>;
  /** Shorthands for a bounded box. */
  h?: number | string;
  w?: number | string;
  maxHeight?: number | string;
  /** The host element. Default `div`. */
  as?: ElementType;
  id?: string;
  'data-testid'?: string;
  /** Legacy `<ScrollArea>` prop: `always` | `auto` | `hover`. Prefer `visibility`. */
  type?: 'always' | 'auto' | 'hover';
  /** Legacy `<ScrollArea>` prop. Prefer `scrollbarMode="reserved"`. */
  offsetScrollbars?: boolean;
}

const OPTION_KEYS: Array<keyof GlideOptions> = [
  'axis', 'visibility', 'scrollbarMode', 'verticalPosition', 'horizontalPosition', 'scrollbarSize', 'thumbHitArea',
  'minThumbLength', 'maxThumbLength', 'overflowTolerance', 'autoUpdate', 'scrollShadows', 'stickToBottom',
  'preserveScrollPosition', 'infiniteScroll', 'overscroll', 'theme', 'events', 'scrollingThreshold', 'focusable',
  'label', 'markers', 'debug'
];

function setRef<T>(ref: Ref<T> | undefined, value: T): void {
  if (!ref) return;
  if (typeof ref === 'function') ref(value);
  else (ref as { current: T }).current = value;
}

/**
 * Mount a Glide on an element you render yourself. The element needs a
 * `.rr-glide__viewport` child (with a `.rr-glide__content` inside) to avoid the
 * engine wrapping your children, which React would not know about.
 *
 * Options are compared by value, so an inline object does not rebuild the
 * engine on every render; handlers are read through a ref, so they may change
 * freely.
 */
export function useGlide(host: RefObject<HTMLElement | null>, options: GlideOptions = {}, handlers: ScrollAreaHandlers = {}): RefObject<Glide | null> {
  const glide = useRef<Glide | null>(null);
  const latest = useRef(handlers);
  latest.current = handlers;
  const key = useMemo(() => stableKey(options), [options]);

  useLayoutEffect(() => {
    const el = host.current;
    if (!el) return;
    const instance = new Glide(el, options);
    glide.current = instance;
    const offs = [
      instance.on('scroll', (e) => latest.current.onScroll?.(e)),
      instance.on('reach-end-y', (e) => latest.current.onReachEnd?.(e)),
      instance.on('reach-start-y', (e) => latest.current.onReachStart?.(e)),
      instance.on('reach-end-x', (e) => latest.current.onReachRight?.(e)),
      instance.on('reach-start-x', (e) => latest.current.onReachLeft?.(e)),
      instance.on('progress', (e) => latest.current.onProgress?.(e)),
      instance.on('bottom-state-change', (e) => latest.current.onBottomStateChange?.(e.current === 1, e)),
      instance.on('resize', (e) => latest.current.onResize?.(e))
    ];
    return () => {
      for (const off of offs) off();
      instance.destroy();
      glide.current = null;
    };
    // The engine is built once per host; option changes go through setOptions below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host]);

  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    glide.current?.setOptions(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return glide;
}

/** Options as a string, minus the callbacks a JSON pass would drop anyway. */
function stableKey(options: GlideOptions): string {
  const { infiniteScroll, ...rest } = options;
  return JSON.stringify({ ...rest, infiniteScroll: infiniteScroll ? { threshold: infiniteScroll.threshold, end: !!infiniteScroll.onReachEnd, start: !!infiniteScroll.onReachStart } : null });
}

/**
 * A scroll container with drawn bars and native scrolling.
 *
 *   <ScrollArea h={320} visibility="auto" stickToBottom="smart" onReachEnd={loadMore}>
 *     …
 *   </ScrollArea>
 *
 * The host is a grid cell that clips; the viewport inside it scrolls. Give the
 * host a height (or let a flex parent with `min-height: 0` size it) and the
 * bars appear when the content is taller.
 */
export const ScrollArea = forwardRef(function ScrollArea(props: ScrollAreaProps, ref: Ref<HTMLElement>): ReactNode {
  const {
    children, className, style, viewportClassName, viewportStyle, viewportProps, viewportRef, glideRef,
    h, w, maxHeight, as: Tag = 'div', id, type, offsetScrollbars,
    onScroll, onReachEnd, onReachStart, onReachRight, onReachLeft, onProgress, onBottomStateChange, onResize,
    'data-testid': testId,
    ...rest
  } = props;
  const host = useRef<HTMLElement | null>(null);
  const viewport = useRef<HTMLDivElement | null>(null);

  const options = useMemo<GlideOptions>(() => {
    const o: GlideOptions = {};
    for (const k of OPTION_KEYS) if (rest[k] !== undefined) (o as Record<string, unknown>)[k] = rest[k];
    if (type && o.visibility === undefined) o.visibility = type === 'auto' ? 'auto' : type;
    if (offsetScrollbars && o.scrollbarMode === undefined) o.scrollbarMode = 'reserved';
    return o;
    // The list is fixed; the values are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...OPTION_KEYS.map((k) => rest[k]), type, offsetScrollbars]);

  const glide = useGlide(host, options, { onScroll, onReachEnd, onReachStart, onReachRight, onReachLeft, onProgress, onBottomStateChange, onResize });

  useImperativeHandle(ref, () => host.current as HTMLElement, []);
  useLayoutEffect(() => { setRef(viewportRef, viewport.current as HTMLDivElement); }, [viewportRef]);
  useLayoutEffect(() => { setRef(glideRef, glide.current); });

  const hostStyle: CSSProperties = {
    ...style,
    height: h != null ? h : style?.height,
    maxHeight: maxHeight != null ? maxHeight : style?.maxHeight,
    width: w != null ? w : style?.width
  };

  // Anything left in `rest` that is not an engine option is a DOM attribute.
  const dom: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(rest)) if (!OPTION_KEYS.includes(k as keyof GlideOptions)) dom[k] = v;

  return (
    <Tag {...dom} ref={host} id={id} className={['rr-glide', className].filter(Boolean).join(' ')} style={hostStyle} data-testid={testId}>
      <div {...viewportProps} ref={viewport} className={['rr-glide__viewport', viewportClassName, viewportProps?.className].filter(Boolean).join(' ')} style={{ ...viewportProps?.style, ...viewportStyle }}>
        <div className="rr-glide__content">{children}</div>
      </div>
    </Tag>
  );
});
